import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_URL } from '../api';

const STORAGE_KEY = 'kp-current-project';

const ProjectCtx = createContext(null);

export function useProjectContext() {
  const ctx = useContext(ProjectCtx);
  if (!ctx) throw new Error('useProjectContext must be used within ProjectContextProvider');
  return ctx;
}

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.id) return parsed;
  } catch {}
  return null;
}

function applyBodyClass(active) {
  if (typeof document === 'undefined') return;
  if (active) document.body.classList.add('project-mode');
  else document.body.classList.remove('project-mode');
}

export function ProjectContextProvider({ children }) {
  const [currentProject, setCurrentProject] = useState(() => readStored());
  const [recentProjects, setRecentProjects] = useState([]);
  const fetchedIdRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Apply body class whenever currentProject toggles.
  useEffect(() => {
    applyBodyClass(!!currentProject);
  }, [currentProject]);

  // Persist to localStorage.
  useEffect(() => {
    try {
      if (currentProject) localStorage.setItem(STORAGE_KEY, JSON.stringify(currentProject));
      else localStorage.removeItem(STORAGE_KEY);
    } catch {}
  }, [currentProject]);

  const enterProject = useCallback((project) => {
    if (!project || !project.id) return;
    const next = {
      id: String(project.id),
      name: String(project.name || project.title || `Projekt #${project.id}`),
      number: project.number || project.project_number || null,
      status: project.status || null,
      // false = testprojekt utan Hogia-koppling (TEST-etikett i sidomenyn).
      hogia_linked: project.hogia_linked ?? null,
    };
    setCurrentProject(next);
    setRecentProjects((cur) => {
      const without = cur.filter((p) => String(p.id) !== next.id);
      return [next, ...without].slice(0, 5);
    });
  }, []);

  const leaveProject = useCallback(() => {
    setCurrentProject(null);
  }, []);

  // Auto-detect project route + auto-leave on global routes.
  useEffect(() => {
    const match = String(location.pathname || '').match(/^\/projects\/(\d+)/);
    if (match) {
      const id = match[1];
      // Already in this project — nothing to do. Ett projekt sparat innan
      // hogia_linked fanns (undefined) hämtas om en gång.
      if (currentProject && String(currentProject.id) === id
        && currentProject.hogia_linked !== undefined) return;
      // Skip duplicate fetches for the same id while it's loading.
      if (fetchedIdRef.current === id) return;
      fetchedIdRef.current = id;
      let cancelled = false;
      (async () => {
        try {
          let tenantSlug = '';
          try { tenantSlug = String(localStorage.getItem('kanplan-active-tenant-slug') || '').trim(); } catch {}
          const res = await fetch(`${API_URL}/projects/${id}`, {
            credentials: 'include',
            headers: tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : {},
          });
          if (!res.ok || cancelled) return;
          const data = await res.json();
          enterProject({
            id: data?.id || id,
            name: data?.name || data?.title,
            number: data?.number || data?.project_number,
            status: data?.status,
            hogia_linked: data?.hogia_linked,
          });
        } catch {
          // Fallback: keep an id-only project so UI still flips into project-mode.
          if (!cancelled) enterProject({ id, name: `Projekt #${id}` });
        }
      })();
      return () => { cancelled = true; };
    }

    // Pathname is NOT a project route — auto-leave if we were in one.
    if (currentProject) {
      // Don't auto-leave on the projects list page itself or while transitioning
      // back via project-aware utility routes (no special-casing for now).
      leaveProject();
    }
    fetchedIdRef.current = null;
    return undefined;
  }, [location.pathname, currentProject, enterProject, leaveProject]);

  const switchProject = useCallback((project) => {
    if (!project || !project.id) return;
    enterProject(project);
    try { navigate(`/projects/${project.id}`); } catch {}
  }, [enterProject, navigate]);

  const value = {
    currentProject,
    recentProjects,
    enterProject,
    leaveProject,
    switchProject,
  };

  return <ProjectCtx.Provider value={value}>{children}</ProjectCtx.Provider>;
}
